// JS of Popup
import { APP_CONFIG } from '../../shared/global/base';
import Overlay from '../../shared/global/Overlay';
import { initializeModules } from '../../shared/global/importer';
import Loader from '../../shared/utils/loader';


type OverlayData = {
    current: Overlay,
    containerDOM: HTMLElement,
    target: HTMLElement,
    loading: boolean,
    loader: Loader,
    overlays: PartialsList,
    onclose: (() => void)[]
}

const OVERLAY_DATA: OverlayData = {
    // overlays data
    current: null,
    containerDOM: null,
    target: null,
    loader: new Loader(),
    loading: false,

    // instances
    overlays: { ...APP_CONFIG.partials, html: { partial: '' } },
    onclose: []
};

const SELECTORS = {
    overlay: 'm-Popup',
    module: 'Popup',
    showpopup: 'popup-visible',
    popupfull: 'popup-full',
    popuptrigger: '[data-js=popup-trigger]',
    wide: 'm-Popup--wide',
    full: 'm-Popup--full',
    showing: 'm-Popup--showing'
};

const WIDE_POPUPS: PartialName[] = ['order'];
const FULL_POPUPS: PartialName[] = ['localchange'];



export { initAfterDynamicImport, showOverlay, initTriggers, closeOverlay, subscribeToOverlayClose, scrollToTop };


function initAfterDynamicImport(dom: HTMLFormElement) {
    OVERLAY_DATA.containerDOM = dom;
    initTriggers({ selector: SELECTORS.popuptrigger });
}


function overlayExists(overlayID: PartialName | null | undefined) {
    return overlayID && OVERLAY_DATA.overlays[overlayID] ? true : false;
}


/**
 * Init partial openers
 * @param {string} selector - optional, default is only for within overlay usage, for external use [data-js=\"popup-trigger\"]
 * @param {DOM element} container - optional
 * @returns {undefined}
 */
function initTriggers({ selector = '[data-partial]', container = document }) {
    let _partialTriggers = container.querySelectorAll(selector);
    if (_partialTriggers.length) {
        _partialTriggers.forEach((el) => {
            const _id = el.getAttribute('data-partial') as PartialName;
            if (overlayExists(_id)) {
                el.addEventListener('click', e => {
                    e.preventDefault();
                    showOverlay(_id);
                });
            }
        });
    }
}


function buildContainer() {
    if (!OVERLAY_DATA.containerDOM) {
        OVERLAY_DATA.containerDOM = document.querySelector(`[data-jsmodule=${SELECTORS.module}]`);
    }
    if (OVERLAY_DATA.containerDOM) {
        if (!OVERLAY_DATA.target) {
            OVERLAY_DATA.target = OVERLAY_DATA.containerDOM.querySelector(`[data-${SELECTORS.module.toLowerCase()}-container]`);

            OVERLAY_DATA.containerDOM.addEventListener('click', overlayClickHandler);
        }

        if (!OVERLAY_DATA.target) {
            return false
        }

        return true;
    }

    return false
}

/**
 *
 * @param overlayID
 */
function showOverlay(overlayID: PartialName, html?: string) {
    // We can just put that html on overlay, without getting partials 
    if (typeof html === 'string') {
        // Check container and create refs if not exists
        if (buildContainer()) {
            // Setup new overlay (always new)
            OVERLAY_DATA.overlays.html.instance = new Overlay('html', {
                overlayContainer: OVERLAY_DATA.containerDOM.parentElement,
                overlayClass: SELECTORS.overlay,
                showClass: SELECTORS.showpopup,
                classFull: SELECTORS.popupfull
            });

            // Save as current
            OVERLAY_DATA.current = OVERLAY_DATA.overlays.html.instance;
            // Set our html on overlay
            OVERLAY_DATA.target.innerHTML = html;
            OVERLAY_DATA.containerDOM.classList.add(SELECTORS.full);
            OVERLAY_DATA.overlays.html.instance.show();
        }
        return;
    }
    if (!OVERLAY_DATA.loading && overlayID) {
        if (overlayExists(overlayID) &&
            (!OVERLAY_DATA.current || OVERLAY_DATA.current.id !== overlayID)) {
            OVERLAY_DATA.loading = true;
            // Remove callbacks for previous popup
            OVERLAY_DATA.onclose = [];

            // Check container and create refs if not exists
            if (buildContainer()) {
                OVERLAY_DATA.target.parentElement.classList.add(SELECTORS.showing);
                OVERLAY_DATA.loader.show(OVERLAY_DATA.target.parentElement, false);
                // Setup new overlay (always new)
                OVERLAY_DATA.overlays[overlayID].instance = new Overlay(overlayID, {
                    overlayContainer: OVERLAY_DATA.containerDOM.parentElement,
                    overlayClass: SELECTORS.overlay,
                    showClass: SELECTORS.showpopup,
                    classFull: SELECTORS.popupfull
                });
                // Save as current
                OVERLAY_DATA.current = OVERLAY_DATA.overlays[overlayID].instance;

                // Modify size classes
                OVERLAY_DATA.containerDOM.classList.remove(SELECTORS.wide);
                OVERLAY_DATA.containerDOM.classList.remove(SELECTORS.full);

                if (WIDE_POPUPS.indexOf(overlayID) > -1) {
                    OVERLAY_DATA.containerDOM.classList.add(SELECTORS.wide);
                }
                else if (FULL_POPUPS.indexOf(overlayID) > -1) {
                    OVERLAY_DATA.containerDOM.classList.add(SELECTORS.full);
                }

                // Load content and create
                OVERLAY_DATA.overlays[overlayID].instance.createAjax(
                    OVERLAY_DATA.overlays[overlayID].partial,
                    OVERLAY_DATA.target,
                    overlayLoaded.bind(null, overlayID),
                    overlayLoadingFailed
                );
            }
            else {
                overlayLoadingFailed();
            }
        }
    }
}




function overlayLoaded(overlayID: string) {
    // initialise overlay functions
    initializeModules(OVERLAY_DATA.target).finally(() => {
        OVERLAY_DATA.target.parentElement.classList.remove(SELECTORS.showing);
        OVERLAY_DATA.loader.hide();
        OVERLAY_DATA.loading = false;
    });
}

function overlayLoadingFailed() {
    // console.log("failed");
    OVERLAY_DATA.loader.hide();
    OVERLAY_DATA.loading = false;

    OVERLAY_DATA.target.parentElement.classList.remove(SELECTORS.showing);
}

function closeOverlay(params?: any) {
    if (OVERLAY_DATA.current) {
        OVERLAY_DATA.current.hide();

        OVERLAY_DATA.onclose.map((f: (p?: any) => void) => f(params));
        OVERLAY_DATA.onclose = [];

        OVERLAY_DATA.current = null;
    }
}

function overlayClickHandler(e: MouseEvent) {
    const _target = e.target as HTMLElement;

    if (_target.hasAttribute('data-jselement-popupclose') ||
        _target.hasAttribute('data-jselement-arrowback')) {
        e.preventDefault();
        closeOverlay();
    }
    else if (_target.hasAttribute('data-partial')) {
        e.preventDefault();
        const _id = _target.getAttribute('data-partial') as PartialName;
        showOverlay(_id);
    }
}

/**
 * Add callback invoked after popup close
 * @param callback
 */
function subscribeToOverlayClose(callback: (p?: any) => void) {
    if (OVERLAY_DATA.current) {
        OVERLAY_DATA.onclose.push(callback);
    }
}

function scrollToTop() {
    OVERLAY_DATA.containerDOM.scrollTo(0, 0);
}
